/**
 * Item id registry. Items live above the block id range (ITEM_BASE..), so a
 * slot's `id` is one number whether it holds a block or an item.
 *
 * The first eleven names keep the ids src/items.ts has always used (saves and
 * the chest loot rely on them); everything after is new. Add new items by
 * APPENDING to NAMES only — never reorder, ids are persisted in saves.
 *
 * Pure data, no imports: safe from workers, the HUD and node smoke tests.
 */

export const ITEM_BASE = 4096;

const NAMES = [
  // --- original items (ids ITEM_BASE+0..10, do not move) ---
  'BUCKET', 'WATER_BUCKET', 'IRON_INGOT', 'GOLD_INGOT', 'COAL', 'BREAD',
  'STICK', 'SADDLE', 'FLINT_AND_STEEL', 'FLINT', 'IRON_NUGGET',
  // --- tools & weapons ---
  'WOODEN_SWORD', 'WOODEN_PICKAXE', 'WOODEN_AXE', 'WOODEN_SHOVEL', 'WOODEN_HOE',
  'STONE_SWORD', 'STONE_PICKAXE', 'STONE_AXE', 'STONE_SHOVEL', 'STONE_HOE',
  'IRON_SWORD', 'IRON_PICKAXE', 'IRON_AXE', 'IRON_SHOVEL', 'IRON_HOE',
  'GOLDEN_SWORD', 'GOLDEN_PICKAXE', 'GOLDEN_AXE', 'GOLDEN_SHOVEL', 'GOLDEN_HOE',
  'DIAMOND_SWORD', 'DIAMOND_PICKAXE', 'DIAMOND_AXE', 'DIAMOND_SHOVEL', 'DIAMOND_HOE',
  'BOW', 'ARROW', 'SHEARS',
  // --- armor ---
  'LEATHER_HELMET', 'LEATHER_CHESTPLATE', 'LEATHER_LEGGINGS', 'LEATHER_BOOTS',
  'IRON_HELMET', 'IRON_CHESTPLATE', 'IRON_LEGGINGS', 'IRON_BOOTS',
  'GOLDEN_HELMET', 'GOLDEN_CHESTPLATE', 'GOLDEN_LEGGINGS', 'GOLDEN_BOOTS',
  'DIAMOND_HELMET', 'DIAMOND_CHESTPLATE', 'DIAMOND_LEGGINGS', 'DIAMOND_BOOTS',
  // --- materials ---
  'DIAMOND', 'EMERALD', 'REDSTONE', 'LAPIS_LAZULI', 'GUNPOWDER', 'STRING', 'BONE',
  'FEATHER', 'LEATHER', 'CHARCOAL', 'COPPER_INGOT', 'RAW_IRON', 'RAW_GOLD', 'RAW_COPPER',
  'CLAY_BALL', 'BRICK', 'WHEAT', 'WHEAT_SEEDS', 'GOLD_NUGGET', 'SPIDER_EYE', 'ROTTEN_FLESH',
  // --- food ---
  'APPLE', 'GOLDEN_APPLE', 'BEEF', 'COOKED_BEEF', 'PORKCHOP', 'COOKED_PORKCHOP',
  'CHICKEN', 'COOKED_CHICKEN', 'MUTTON', 'COOKED_MUTTON', 'MELON_SLICE', 'COOKIE',
  // --- later additions ---
  'SHIELD',
  // --- nether ---
  'QUARTZ', 'NETHERITE_SCRAP', 'NETHERITE_INGOT', 'BLAZE_ROD', 'BLAZE_POWDER', 'GLOWSTONE_DUST',
  // --- combat & enchanting ---
  'NETHERITE_SWORD', 'NETHERITE_PICKAXE', 'NETHERITE_AXE', 'NETHERITE_SHOVEL', 'NETHERITE_HOE',
  'NETHERITE_HELMET', 'NETHERITE_CHESTPLATE', 'NETHERITE_LEGGINGS', 'NETHERITE_BOOTS',
  'MACE', 'TRIDENT', 'BOOK', 'PAPER', 'ENCHANTED_BOOK', 'NETHERITE_UPGRADE_SMITHING_TEMPLATE',
  'COPPER_SWORD', 'COPPER_PICKAXE', 'COPPER_AXE', 'COPPER_SHOVEL', 'COPPER_HOE',
  'COPPER_HELMET', 'COPPER_CHESTPLATE', 'COPPER_LEGGINGS', 'COPPER_BOOTS',
  'HEAVY_CORE', 'BREEZE_ROD', 'CROSSBOW',
  // --- spawn eggs (game/spawnEggs.ts) ---
  'ZOMBIE_SPAWN_EGG', 'SKELETON_SPAWN_EGG', 'CREEPER_SPAWN_EGG', 'SPIDER_SPAWN_EGG',
  'COW_SPAWN_EGG', 'PIG_SPAWN_EGG', 'SHEEP_SPAWN_EGG', 'CHICKEN_SPAWN_EGG',
  'ZOMBIFIED_PIGLIN_SPAWN_EGG', 'PIGLIN_SPAWN_EGG', 'MAGMA_CUBE_SPAWN_EGG',
  // --- animals ---
  'EGG',
  // --- nether, round 2 ---
  'GHAST_TEAR', 'MAGMA_CREAM', 'NETHER_BRICK', 'FIRE_CHARGE',
  // --- farming (world/crops.ts) ---
  'CARROT', 'POTATO', 'BAKED_POTATO', 'POISONOUS_POTATO', 'BEETROOT', 'BEETROOT_SEEDS', 'GOLDEN_CARROT',
  // --- spawn eggs, nether round 2 (game/spawnEggs.ts) ---
  'GHAST_SPAWN_EGG', 'BLAZE_SPAWN_EGG', 'WITHER_SKELETON_SPAWN_EGG', 'HOGLIN_SPAWN_EGG', 'STRIDER_SPAWN_EGG', 'ENDERMAN_SPAWN_EGG',
  'BONE_MEAL',
  // --- the End path ---
  'ENDER_PEARL',
  // --- villages & illagers (entities/village) ---
  'VILLAGER_SPAWN_EGG', 'PILLAGER_SPAWN_EGG', 'ZOMBIE_VILLAGER_SPAWN_EGG', 'VINDICATOR_SPAWN_EGG',
  // --- potions & brewing (game/brewing): the potion type rides in Stack.dmg ---
  'GLASS_BOTTLE', 'POTION', 'SPLASH_POTION', 'LINGERING_POTION', 'FERMENTED_SPIDER_EYE', 'SUGAR',
  'RABBIT_FOOT', 'GLISTERING_MELON_SLICE', 'PHANTOM_MEMBRANE', 'PUFFERFISH', 'MILK_BUCKET', 'HONEY_BOTTLE',
  'SUSPICIOUS_STEW', 'ENCHANTED_GOLDEN_APPLE', 'DRAGON_BREATH', 'BOWL', 'TURTLE_SCUTE',
  // --- the End (game/endPortal.ts) ---
  'EYE_OF_ENDER',
  // --- overworld hostiles round 2 (entities/overworld2) ---
  'SLIME_BALL', 'SLIME_SPAWN_EGG', 'CAVE_SPIDER_SPAWN_EGG', 'HUSK_SPAWN_EGG', 'STRAY_SPAWN_EGG',
  'DROWNED_SPAWN_EGG', 'SILVERFISH_SPAWN_EGG', 'ENDERMITE_SPAWN_EGG', 'WOLF_SPAWN_EGG',
  // --- structures round 2 (worldgen/structures, game/trials.ts) ---
  'CHORUS_FRUIT', 'POPPED_CHORUS_FRUIT', 'ELYTRA', 'TRIAL_KEY',
  // --- mobs round 2b (entities/overworld2) ---
  'WITCH_SPAWN_EGG', 'PHANTOM_SPAWN_EGG', 'CAT_SPAWN_EGG', 'SQUID_SPAWN_EGG', 'COD_SPAWN_EGG', 'SALMON_SPAWN_EGG', 'BAT_SPAWN_EGG',
  // --- End polish ---
  'END_CRYSTAL',
  // --- item use (game/itemUse) ---
  'LAVA_BUCKET', 'TOTEM_OF_UNDYING', 'RESERVED_DUP_NAME_TAG', // slot was a stray duplicate NAME_TAG (I.NAME_TAG is the later slot); never renumber
  // --- spears (game/spear.ts) ---
  'WOODEN_SPEAR', 'STONE_SPEAR', 'COPPER_SPEAR', 'IRON_SPEAR', 'GOLDEN_SPEAR', 'DIAMOND_SPEAR', 'NETHERITE_SPEAR',
  // --- vehicles & fishing (entities/vehicles, game/fishing.ts) ---
  'FISHING_ROD', 'COD', 'SALMON', 'TROPICAL_FISH', 'COOKED_COD', 'COOKED_SALMON', 'NAME_TAG', 'NAUTILUS_SHELL', 'INK_SAC',
  'LEAD', 'MINECART',
  'OAK_BOAT', 'SPRUCE_BOAT', 'BIRCH_BOAT', 'JUNGLE_BOAT', 'ACACIA_BOAT', 'DARK_OAK_BOAT', 'MANGROVE_BOAT', 'CHERRY_BOAT', 'PALE_OAK_BOAT',
  // --- riding (game/mounts.ts) ---
  'CARROT_ON_A_STICK', 'WARPED_FUNGUS_ON_A_STICK',
  'LEATHER_HORSE_ARMOR', 'IRON_HORSE_ARMOR', 'GOLDEN_HORSE_ARMOR', 'DIAMOND_HORSE_ARMOR',
  // --- elytra rockets ---
  'FIREWORK_ROCKET', 'FIREWORK_STAR',
  // --- lush caves (worldgen/features.ts) ---
  'GLOW_BERRIES',
  // --- jungle mobs (entities/overworld2/jungle.ts) ---
  'PANDA_SPAWN_EGG', 'PARROT_SPAWN_EGG', 'FOX_SPAWN_EGG',
  // --- wither boss (entities/boss/wither.ts) ---
  'NETHER_STAR', 'WITHER_SPAWN_EGG',
  // --- item audit: riding spawn eggs ---
  'HORSE_SPAWN_EGG', 'DONKEY_SPAWN_EGG',
  // --- warden (entities/boss/warden.ts) ---
  'WARDEN_SPAWN_EGG',
  // --- wandering trader + llamas (entities/trader) ---
  'WANDERING_TRADER_SPAWN_EGG', 'LLAMA_SPAWN_EGG', 'TRADER_LLAMA_SPAWN_EGG',
  // roster 3 mobs (src/entities/mobs3)
  'RABBIT', 'COOKED_RABBIT', 'RABBIT_HIDE', 'GOAT_HORN', 'SNOWBALL', 'MUSHROOM_STEW', 'PRISMARINE_SHARD', 'PRISMARINE_CRYSTALS', 'SHULKER_SHELL',
  'RABBIT_SPAWN_EGG', 'GOAT_SPAWN_EGG', 'TURTLE_SPAWN_EGG', 'MOOSHROOM_SPAWN_EGG', 'POLAR_BEAR_SPAWN_EGG', 'OCELOT_SPAWN_EGG', 'SNOW_GOLEM_SPAWN_EGG',
  'AXOLOTL_SPAWN_EGG', 'DOLPHIN_SPAWN_EGG', 'GUARDIAN_SPAWN_EGG', 'ELDER_GUARDIAN_SPAWN_EGG', 'SHULKER_SPAWN_EGG', 'EVOKER_SPAWN_EGG', 'VEX_SPAWN_EGG',
  'RAVAGER_SPAWN_EGG', 'ZOGLIN_SPAWN_EGG', 'PIGLIN_BRUTE_SPAWN_EGG',
] as const;

export type ItemName = typeof NAMES[number];

/** `I.DIAMOND_SWORD` etc. — numeric item ids. */
export const I = Object.fromEntries(NAMES.map((n, i) => [n, ITEM_BASE + i])) as Record<ItemName, number>;

/** Every registered item id, in registry order. */
export const ALL_ITEM_IDS: readonly number[] = NAMES.map((_, i) => ITEM_BASE + i).filter((id) => !NAMES[id - ITEM_BASE].startsWith('RESERVED_'));

/** Registry name of an item id ('DIAMOND_SWORD'), or undefined for blocks / unknown ids. */
export function itemNameOf(id: number): ItemName | undefined {
  return NAMES[id - ITEM_BASE];
}
